// cart-drawer.js — Boldpurity
import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:updated', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:updated', this.#handleCartUpdate);
  }

  // ── Section refresh ──────────────────────────────────────────────
  async refresh() {
    try {
      const [cartResponse, sectionResponse] = await Promise.all([
        fetch('/cart.js'),
        this.#getSectionHTML()
      ]);

      if (!cartResponse.ok) return;
      const cartData = await cartResponse.json();
      window.cart = cartData;

      if (sectionResponse) {
        this.#applyNewHTML(sectionResponse, cartData);
      } else {
        await this.#updateDialog();
      }

      this.#initUpsell();
    } catch (err) {
      console.warn('[CartDrawer] refresh failed', err);
    }
  }

  async #getSectionHTML() {
    try {
      const sectionId = this.getAttribute('data-section-id');
      if (!sectionId) return null;
      const res = await fetch(`/sections/cart-drawer?section_id=${sectionId}`);
      return res.ok ? res.text() : null;
    } catch {
      return null;
    }
  }

  #applyNewHTML(html, cartData) {
    const inner = this.querySelector('.cart-drawer__inner');
    if (!inner) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newInner = doc.querySelector('.cart-drawer__inner');
    if (newInner) {
      inner.outerHTML = newInner.outerHTML;
      this.#updateBubble(cartData);
    }
  }

  async #updateDialog() {
    const inner = this.querySelector('.cart-drawer__inner');
    const sectionId = this.getAttribute('data-section-id');
    if (!inner || !sectionId) return;

    try {
      const res = await fetch(`/sections/cart-drawer?section_id=${sectionId}`);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newInner = doc.querySelector('.cart-drawer__inner');
      if (newInner) {
        inner.outerHTML = newInner.outerHTML;
        this.#updateBubble();
      }
    } catch (err) {
      console.warn('[CartDrawer] dialog update failed', err);
    }
  }

  async #updateBubble(cartData = null) {
    try {
      let count = cartData?.item_count;

      if (count === undefined) {
        const res = await fetch('/cart.js');
        if (!res.ok) return;
        const data = await res.json();
        count = data.item_count;
      }

      const bubble = document.querySelector('.cart-bubble');
      if (!bubble) return;

      const countEl = bubble.querySelector('.cart-bubble__text-count');
      const hiddenEl = bubble.querySelector('.visually-hidden');

      if (countEl) countEl.textContent = count;
      if (hiddenEl) hiddenEl.textContent = `Total items in cart: ${count}`;

      if (count > 0) {
        countEl?.classList.remove('hidden');
        bubble.classList.remove('visually-hidden');
      } else {
        countEl?.classList.add('hidden');
        bubble.classList.add('visually-hidden');
      }
    } catch (err) {
      console.warn('[CartDrawer] bubble update failed', err);
    }
  }

  // ── Event handlers ───────────────────────────────────────────────
  #handleCartAdd = async () => {
    if (this.hasAttribute('auto-open')) {
      // this.showDialog();
      handleFloCartBtn();
      await this.refresh();
    }
  };

  #handleCartUpdate = async () => {
    await this.refresh();
  };

  // ── Public API ───────────────────────────────────────────────────
  async open() {
    // this.showDialog();
    handleFloCartBtn();
    await this.refresh();

    // Close if installments CTA is clicked (avoids overlapping dialogs)
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const shadow = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = shadow?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // ── Upsell add-to-cart ────────────────────────────────────────────
  #initUpsell() {
    const buttons = this.querySelectorAll('.cart_upsell_drawer');

    buttons.forEach(btn => {
      // Avoid double-binding
      if (btn.dataset.upsellBound) return;
      btn.dataset.upsellBound = '1';

      btn.addEventListener('click', async () => {
        const card = btn.closest('.product-item-cart');
        if (!card) return;

        const variantId = card.getAttribute('data-variant-id');
        if (!variantId) return;

        const label = btn.querySelector('.add-text');
        const originalLabel = label?.textContent || 'Add';

        btn.disabled = true;
        if (label) label.textContent = 'Adding…';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });

          if (res.ok) {
            if (label) label.textContent = 'Added ✓';
            document.dispatchEvent(new CustomEvent('cart:updated'));
            setTimeout(() => {
              if (label) label.textContent = originalLabel;
              btn.disabled = false;
            }, 1800);
          } else {
            throw new Error('add failed');
          }
        } catch {
          if (label) label.textContent = 'Error';
          setTimeout(() => {
            if (label) label.textContent = originalLabel;
            btn.disabled = false;
          }, 1800);
        }
      });
    });
  }

  // Alias used by connectedCallback-based calls after DOM refresh
  initializeUpsell() {
    this.#initUpsell();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
